import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Drawer, Empty, useTheme } from '@prio365/prio365-react-library';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Flex from '../../../../../components/Flex';
import { PrioShareRecipientDto } from '../../../../../models/PrioShare';
import { PrioTheme } from '../../../../../theme/types';
import { makePrioStyles } from '../../../../../theme/utils';
import { formatDateTime } from '../../../../../util/helpers';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  recipientHeader: {
    padding: theme.spacing.regular,
    borderRadius: theme.borderRadius.regular,
    backgroundColor: theme.colors.application.background.light,
  },
  recipientEmail: {
    fontSize: theme.font.fontSize.small,
    fontWeight: 600,
    color: theme.colors.application.typography.default,
  },
  recipientLink: {
    fontSize: theme.font.fontSize.extraSmall,
    color: theme.colors.application.typography.muted,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  card: {
    padding: theme.spacing.regular,
    borderRadius: theme.borderRadius.regular,
    border: `1px solid ${theme.colors.application.border}`,
    backgroundColor: theme.colors.application.background.default,
  },
  cardHeader: {
    fontSize: theme.font.fontSize.small,
    fontWeight: 600,
    color: theme.colors.application.typography.default,
  },
  successIcon: {
    color: theme.colors.base.green.default,
  },
  failIcon: {
    color: theme.colors.base.red.default,
  },
  timeline: {
    position: 'relative' as const,
    paddingLeft: 28,
    marginTop: theme.spacing.small,
  },
  timelineLine: {
    position: 'absolute' as const,
    left: 9,
    top: 10,
    bottom: 10,
    width: 2,
    backgroundColor: theme.colors.application.border,
  },
  timelineItem: {
    position: 'relative' as const,
    paddingBottom: theme.spacing.small,
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  timelineDot: {
    position: 'absolute' as const,
    left: -28,
    top: 2,
    width: 20,
    height: 20,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    fontSize: 10,
  },
  timelineDotSuccess: {
    backgroundColor: theme.colors.base.green.default,
    color: '#fff',
  },
  timelineDotFail: {
    backgroundColor: theme.colors.base.red.default,
    color: '#fff',
  },
  timelineDotNeutral: {
    backgroundColor: theme.colors.base.blue.default,
    color: '#fff',
  },
  timelineLabel: {
    fontSize: theme.font.fontSize.extraSmall,
    color: theme.colors.application.typography.muted,
    lineHeight: 1.2,
  },
  timelineValue: {
    fontSize: theme.font.fontSize.extraSmall,
    color: theme.colors.application.typography.default,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  downloadList: {
    flex: 1,
    overflowY: 'auto' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing.regular,
    paddingTop: theme.spacing.regular,
  },
  warningBadge: {
    fontSize: theme.font.fontSize.extraSmall,
    color: theme.colors.base.yellow.default,
    fontWeight: 600,
  },
}));

interface PrioShareRecipientDrawerProps {
  recipient: PrioShareRecipientDto | null;
  visible: boolean;
  onClose: () => void;
}

const PrioShareRecipientDrawer: React.FC<PrioShareRecipientDrawerProps> = ({
  recipient,
  visible,
  onClose,
}) => {
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();

  const tKey = (key: string) =>
    t(`common:widgetArea.prioShareWidget.detailView.${key}`);

  const sortedDownloads = useMemo(
    () =>
      [...(recipient?.downloads ?? [])].sort(
        (a, b) =>
          new Date(b.acceptedAgbAt).getTime() -
          new Date(a.acceptedAgbAt).getTime()
      ),
    [recipient?.downloads]
  );

  if (!recipient) return null;

  return (
    <Drawer
      title={tKey('recipientDrawerTitle')}
      visible={visible}
      onClose={onClose}
      closable
    >
      <Flex.Column style={{ height: '100%', overflow: 'hidden' }}>
        <div className={classes.recipientHeader}>
          <Flex.Row alignItems="center" childrenGap={theme.spacing.small}>
            <FontAwesomeIcon
              icon={['fal', 'user']}
              style={{ color: theme.colors.application.typography.muted }}
            />
            <Flex.Column>
              <span className={classes.recipientEmail}>
                {recipient.email}
              </span>
              {recipient.link && (
                <span className={classes.recipientLink}>
                  {recipient.link}
                </span>
              )}
            </Flex.Column>
          </Flex.Row>
        </div>

        <div className={classes.downloadList}>
          {sortedDownloads.length === 0 ? (
            <Empty customMessage={tKey('noDownloads')} />
          ) : (
            sortedDownloads.map((download, index) => (
              <div key={download.prioShareDownloadId} className={classes.card}>
                <Flex.Row alignItems="center" justifyContent="space-between">
                  <Flex.Row
                    alignItems="center"
                    childrenGap={theme.spacing.small}
                  >
                    <FontAwesomeIcon
                      icon={[
                        'fal',
                        download.downloadSuccessful
                          ? 'circle-check'
                          : 'circle-xmark',
                      ]}
                      className={
                        download.downloadSuccessful
                          ? classes.successIcon
                          : classes.failIcon
                      }
                    />
                    <span className={classes.cardHeader}>
                      {t(
                        'common:widgetArea.prioShareWidget.detailView.downloadNumber',
                        { number: sortedDownloads.length - index }
                      )}
                    </span>
                  </Flex.Row>
                  {download.wrongCodeAttempts > 0 && (
                    <Flex.Row
                      alignItems="center"
                      childrenGap={theme.spacing.extraSmall}
                    >
                      <FontAwesomeIcon
                        icon={['fal', 'triangle-exclamation']}
                        style={{ color: theme.colors.base.yellow.default }}
                      />
                      <span className={classes.warningBadge}>
                        {download.wrongCodeAttempts} {tKey('wrongCodeAttempts')}
                      </span>
                    </Flex.Row>
                  )}
                </Flex.Row>
                <div className={classes.timeline}>
                  <div className={classes.timelineLine} />
                  <div className={classes.timelineItem}>
                    <div
                      className={`${classes.timelineDot} ${classes.timelineDotNeutral}`}
                    >
                      <FontAwesomeIcon icon={['fal', 'file-lines']} />
                    </div>
                    <div className={classes.timelineLabel}>
                      {tKey('acceptedAgbAt')}
                    </div>
                    <div className={classes.timelineValue}>
                      {formatDateTime(download.acceptedAgbAt) || '—'}
                    </div>
                  </div>
                  <div className={classes.timelineItem}>
                    <div
                      className={`${classes.timelineDot} ${classes.timelineDotNeutral}`}
                    >
                      <FontAwesomeIcon icon={['fal', 'key']} />
                    </div>
                    <div className={classes.timelineLabel}>
                      {tKey('codeCreationDate')}
                    </div>
                    <div className={classes.timelineValue}>
                      {formatDateTime(download.codeCreationDate) || '—'}
                    </div>
                  </div>
                  <div className={classes.timelineItem}>
                    <div
                      className={`${classes.timelineDot} ${
                        download.downloadSuccessful
                          ? classes.timelineDotSuccess
                          : classes.timelineDotFail
                      }`}
                    >
                      <FontAwesomeIcon icon={['fal', 'download']} />
                    </div>
                    <div className={classes.timelineLabel}>
                      {download.downloadSuccessful
                        ? tKey('downloadDate')
                        : tKey('downloadFailed')}
                    </div>
                    <div className={classes.timelineValue}>
                      {formatDateTime(download.downloadDate) || '—'}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Flex.Column>
    </Drawer>
  );
};

export default PrioShareRecipientDrawer;
